import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { BehaviorSubject, Observable, of } from 'rxjs';
import { catchError, filter, map } from 'rxjs/operators';
import { User } from '../models/user.model';
import { environment } from 'src/environments/environment';
import { Router } from '@angular/router';
import { OnboardingService } from './onboarding.service';

@Injectable({
  providedIn: 'root'
})
export class UserService {
  
  private baseUrl = environment.baseUrl;

  isLoggedIn = new BehaviorSubject<boolean>(!!this.getToken());
  private userSubject = new BehaviorSubject<any>(null);

  private repeatCardsCountSubject = new BehaviorSubject<any | null>(null);

  constructor(
    private http: HttpClient,
    private router: Router,
    private onboardingService: OnboardingService
  ) {}

  loadUser(): Observable<any> {
    if (!this.isLoggedIn) {
      this.userSubject.next(null);
      return of(null);
    }
  
    return this.http.get<any>(`${this.baseUrl}/auth/user`).pipe(
      map(user => {
        const loadedUser: User = User.constructUser(user);
        this.userSubject.next(User.constructUser(loadedUser));
        if (!loadedUser.onboardingDone) {
          this.router.navigate(['onboarding']);
        }
        this.onboardingService.loadOnboarding();
        this.fetchRepeatCardsCount();
        return loadedUser;
      }),
      catchError(() => {
        this.userSubject.next(null);
        return of(null);
      })
    );
  }

  getUser(): Observable<User> {
    return this.userSubject.asObservable();
  }

  getToken(): string | null {
    return localStorage.getItem('token');
  }

  updateLastOnline() {
    return this.http.post<any>(`${this.baseUrl}/user/last-online`, {});
  }

  updateProfile(user: User) {
    return this.http.post<any>(`${this.baseUrl}/user/update-profile`, { user });
  }

  fetchRepeatCardsCount() {
    this.http.get<any>(`${this.baseUrl}/user/repeat-cards-count`)
      .subscribe(res => this.repeatCardsCountSubject.next(res));
  }

  getRepeatCardsCount(): Observable<number | null> {
    return this.repeatCardsCountSubject.asObservable().pipe(
      filter(res => res !== null)
    );
  }

  getWeeklyGoalCount() {
    return this.http.get<any>(`${this.baseUrl}/user/weekly-goal-count`);
  }

  getMasteredCardsCount() {
    return this.http.get<any>(`${this.baseUrl}/user/mastered-cards-count`);
  }

  setPremiumPromptShown() {
    return this.http.post<any>(`${this.baseUrl}/user/premium-prompt-shown`, {});
  }

  logout(): void {
    localStorage.removeItem('token');
    this.userSubject.next(null);
    this.isLoggedIn.next(false);
    this.router.navigate(['login']);
  }

  removeToken() {
    localStorage.removeItem('token');
  }
}